import axios from "axios";

// CRA only exposes env vars prefixed with REACT_APP_ (set in client/.env)
export const BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://transworldshipping-api.vercel.app";

const TOKEN_KEY = "token";
const USER_KEY = "authUser";

// Shared axios instance - attaches the bearer token to every request
export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
};

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setStoredUser = (user) => {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const isLoggedIn = () => !!getToken();

export const isAdmin = () => getStoredUser()?.role === "admin";

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Logs in, then fetches the current user's profile (id/email/role) and caches it
export const login = async (email, password) => {
  const { data } = await axios.post(
    `${BASE_URL}/api/user/login`,
    { email, password },
    { withCredentials: true },
  );

  if (!data.success) {
    throw new Error(data.message || "Login failed");
  }

  setToken(data.data?.token);

  const profile = await api.get("/api/user/profile");
  const user = profile.data?.data;
  setStoredUser(user);

  return user;
};

export const logout = async () => {
  try {
    await api.post("/api/user/logout");
  } catch {
    // best-effort - still clear local state even if the request fails
  } finally {
    clearAuth();
  }
};

const authServices = {
  BASE_URL,
  api,
  getToken,
  setToken,
  getStoredUser,
  setStoredUser,
  isLoggedIn,
  isAdmin,
  clearAuth,
  login,
  logout,
};

export default authServices;
